import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { ArrowDownToLineIcon, X } from "lucide-react";
import { cn } from '@/lib/utils';
import { CopyClipboard } from '@/components/common/copy-clipboard';
import { TextButton } from '@/components/common/text-button';
import { OrderUserData } from '@/services/agent-v2-order-user-list';
import { formatToFullDateTime } from '@/utils/date-formatter';
import { TruncatedText } from '@/components/common/truncated-text';
import { Image } from '@/components/common/image';
import { IconButton } from '@/components/common/icon-button';
import { LoadingOverlay } from '../common/loading-overlay';
import { agentOrderHooks } from '@/manager';
import { FileBgIcon } from './assets';
import { getOrderUserStatusBadgeClass } from './order-user-status';

interface OrderDetailModalProps {
  trigger?: React.ReactElement;
  orderDetail?: OrderUserData;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSave?: (data) => void;
}

/** 订单详情弹窗 */
export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  trigger,
  orderDetail = {} as OrderUserData,
  open: externalOpen,
  onOpenChange,
  onSave,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null); // 用于聚焦标题的ref
  const { downLoad, loading: downloading } = agentOrderHooks.useDownloadAttachment();

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  const handleDownload = async () => {
    if (!orderDetail.orderAttachment) return;

    await downLoad(orderDetail.orderAttachment, orderDetail.orderAttachmentName);
  }

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  const orderStatusLabel = (status: number | undefined) => {
    if (status == null || Number.isNaN(Number(status))) return '—';
    return t(`agent_dashboard.order_management.table.order_status_map.${status}`, { defaultValue: '—' });
  };

  const orderTypeMap = {
    '1': t('agent_dashboard.order_management.table.order_type_map.year_vip'),
    '2': t('agent_dashboard.order_management.table.order_type_map.half_year_vip'),
    '3': t('agent_dashboard.order_management.table.order_type_map.quarter_vip'),
  }

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[700px] h-[880px]  flex-shrink-0 overflow-hidden",
          "z-50"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-[30px] pb-[6px]">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none'
          >
            <div className="text-[rgba(24,29,39,1)] text-lg font-semibold">
              {t('agent_dashboard.order_management.detail_modal.title')}
            </div>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>

        {/* 详情区域 */}
        <div className='flex-1 px-6 pt-[10px] space-y-5 overflow-y-auto pb-5'>
          {/* 订单信息 */}
          <div>
            <div className='flex items-center mb-4'>
              <h2 className="text-[rgba(24,29,39,1)] text-base font-semibold">
                {t('agent_dashboard.order_management.detail_modal.order_info.title')}
              </h2>
            </div>
            <div className='grid grid-cols-2 gap-5'>
              <div>
                <span className='text-[rgba(100,116,139,1)] text-sm'>
                  {t('agent_dashboard.order_management.detail_modal.order_info.order_no')}
                </span>
                <div className='text-[rgba(24,29,39,1)] mt-2'>
                  <span className="break-all text-sm">
                    {orderDetail?.orderNo || '-'}
                    <CopyClipboard
                      text={orderDetail?.orderNo}
                      className='inline-flex align-bottom ml-2'
                    />
                  </span>

                </div>
              </div>
              <div>
                <span className='text-[rgba(100,116,139,1)] text-sm'>
                  {t('agent_dashboard.order_management.detail_modal.order_info.status')}
                </span>
                <div className='text-[rgba(24,29,39,1)] mt-2 text-sm'>
                  <span
                    className={cn(
                      'px-2 py-1 rounded-full text-medium',
                      getOrderUserStatusBadgeClass(orderDetail?.status),
                    )}>
                    {orderStatusLabel(orderDetail?.status)}
                  </span>
                </div>
              </div>
              <div>
                <span className='text-[rgba(100,116,139,1)] text-sm'>
                  {t('agent_dashboard.order_management.detail_modal.order_info.create_time')}
                </span>
                <div className='text-[rgba(24,29,39,1)] mt-2 text-sm'>{orderDetail?.createdAt ? formatToFullDateTime(orderDetail?.createdAt) : '-'}</div>
              </div>
              <div>
                <span className='text-[rgba(100,116,139,1)] text-sm'>
                  {t('agent_dashboard.order_management.detail_modal.order_info.expire_time')}
                </span>
                <div className='text-[rgba(24,29,39,1)] mt-2 text-sm'>{orderDetail?.expireAt ? formatToFullDateTime(orderDetail?.expireAt) : '-'}</div>
              </div>
              <div>
                <span className='text-[rgba(100,116,139,1)] text-sm'>
                  {t('agent_dashboard.order_management.detail_modal.order_info.remarks')}
                </span>
                <div className='text-[rgba(24,29,39,1)] mt-2 text-sm'>{orderDetail?.description || '-'}</div>
              </div>
              <div>
                <span className='text-[rgba(100,116,139,1)] text-sm'>
                  {t('agent_dashboard.order_management.detail_modal.order_info.order_attachment')}
                </span>
                <div className='mt-4 select-none relative'>
                  {!orderDetail?.orderAttachment && '-'}
                  {orderDetail?.orderAttachmentName && (
                    <div className='w-[7.625rem] h-[94px] relative'>
                      <div className='flex items-center' >
                        {['png', 'jpeg', 'jpg'].includes(orderDetail?.orderAttachmentType || '') && (
                          <div className='size-[4.25rem] relative rounded-[8px] flex-shrink-0'>
                            <Image
                              src={orderDetail?.orderAttachment}
                              alt={orderDetail?.orderAttachmentName}
                              className='size-full rounded-[8px] object-cover'
                            />
                          </div>
                        )}
                        {['pdf', 'doc', 'docx', 'webp'].includes(orderDetail?.orderAttachmentType || '') && (
                          <div className='size-[4.25rem] relative rounded-[12px]'>
                            <div className='relative'>
                              <FileBgIcon />
                              <div className='absolute top-[26px] left-0 px-[4.5px] py-[1px] rounded-sm text-[15px] font-semibold text-white bg-[rgba(55,93,251,1)] tracking-[30%]'>
                                {orderDetail?.orderAttachmentType}
                              </div>
                            </div>
                          </div>
                        )}
                        <IconButton
                          icon={<ArrowDownToLineIcon className='size-5' />}
                          className=' text-black cursor-pointer size-6 ml-3 hover:bg-black/10'
                          onClick={handleDownload}
                        />
                      </div>
                      <TruncatedText text={orderDetail?.orderAttachmentName} className='w-[100%] text-xs text-[rgba(16,24,40,1)] mt-2' />
                      <LoadingOverlay loading={downloading} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* 用户信息 */}
          <div>
            <div className='flex items-center mb-4'>
              <h2 className="text-[rgba(24,29,39,1)] text-lg font-semibold">
                {t('agent_dashboard.order_management.detail_modal.user_info.title')}
              </h2>
            </div>
            <div className='grid grid-cols-2 gap-4 '>
              <div>
                <span className='text-[rgba(100,116,139,1)] text-sm'>
                  {t('agent_dashboard.order_management.detail_modal.user_info.user_name')}
                </span>
                <div className='text-[rgba(24,29,39,1)] mt-2 text-sm'>{orderDetail?.userName || '-'}</div>
              </div>
              <div>
                <span className='text-[rgba(100,116,139,1)] text-sm'>
                  {t('agent_dashboard.order_management.detail_modal.user_info.user_id')}
                </span>
                <div className="text-[rgba(24,29,39,1)] mt-2 text-sm">
                  <span className="break-all">
                    {orderDetail?.userId || '-'}
                    <CopyClipboard
                      text={orderDetail?.userId}
                      className="inline-flex align-bottom ml-2"
                    />
                  </span>
                </div>
              </div>
              <div>
                <span className='text-[rgba(100,116,139,1)] text-sm'>
                  {t('agent_dashboard.order_management.detail_modal.user_info.user_email')}
                </span>
                <div className='text-[rgba(24,29,39,1)] mt-2 text-sm'>{orderDetail?.email}</div>
              </div>
            </div>
          </div>
          {/* 商品信息 */}
          <div>
            <div className='flex items-center mb-4'>
              <h2 className="text-[rgba(24,29,39,1)] text-lg font-semibold">
                {t('agent_dashboard.order_management.detail_modal.goods_info.title')}
              </h2>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <span className='text-[rgba(100,116,139,1)] text-sm'>
                  {t('agent_dashboard.order_management.detail_modal.goods_info.goods_name')}
                </span>
                <div className='text-[rgba(24,29,39,1)] mt-2 text-sm'>{orderDetail?.productName}</div>
              </div>
              <div>
                <span className='text-[rgba(100,116,139,1)] text-sm'>
                  {t('agent_dashboard.order_management.detail_modal.goods_info.agent_type')}
                </span>
                <div className='text-[rgba(24,29,39,1)] mt-2 text-sm'>{orderDetail?.agentName}</div>
              </div>
              <div className='col-span-2'>
                <span className='text-[rgba(100,116,139,1)] text-sm'>
                  {t('agent_dashboard.order_management.detail_modal.goods_info.validity_period')}
                </span>
                <div className='text-[rgba(24,29,39,1)] mt-2 text-sm'>
                  {formatToFullDateTime(orderDetail?.createdAt)} - {formatToFullDateTime(orderDetail?.expireAt)}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex justify-end p-6 space-x-3 border-t border-[rgba(233,234,235,1)]'>
          <TextButton
            label={t('agent_dashboard.user_management.create_modal.cancel')}
            className='w-[147px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
            onClick={handleClose}
          />
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
